import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import type { D2DHouse, D2DUnit, D2DStreet } from '@/types/d2d';

interface UseRealtimeSyncProps {
  userId?: string;
  currentAreaId?: string | null;
  currentStreetId?: string | null;
  currentHouseId?: string | null;
  onStreetInserted?: (street: D2DStreet) => void;
  onStreetUpdated?: (street: D2DStreet) => void;
  onHouseInserted?: (house: D2DHouse) => void;
  onHouseUpdated?: (house: D2DHouse) => void;
  onUnitInserted?: (unit: D2DUnit) => void;
  onUnitUpdated?: (unit: D2DUnit) => void;
}

// Track recently created items by this session to avoid duplicates
const recentlyCreatedItems = new Set<string>();

export const addRecentlyCreatedItem = (id: string) => {
  recentlyCreatedItems.add(id);
  // Clean up after 10 seconds
  setTimeout(() => recentlyCreatedItems.delete(id), 10000);
};

export const useRealtimeSync = ({
  userId,
  currentAreaId,
  currentStreetId,
  currentHouseId,
  onStreetInserted,
  onStreetUpdated,
  onHouseInserted,
  onHouseUpdated,
  onUnitInserted,
  onUnitUpdated,
}: UseRealtimeSyncProps) => {
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    if (!userId) return;

    // Clean up previous channel
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    const channel = supabase.channel('d2d-realtime-sync');

    // Listen for street changes when viewing an area
    if (currentAreaId) {
      channel.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'd2d_streets',
          filter: `area_id=eq.${currentAreaId}`,
        },
        (payload) => {
          const newStreet = payload.new as D2DStreet;
          
          if (recentlyCreatedItems.has(newStreet.id)) {
            console.log('🛣️ Skipping street we just created:', newStreet.id);
            return;
          }
          
          if (newStreet.user_id !== userId) {
            console.log('🛣️ New street created by another user:', newStreet);
            toast({
              title: "Neue Straße",
              description: `Ein Teammitglied hat die Straße "${newStreet.name}" erstellt.`,
            });
          } else {
            console.log('🛣️ New street from same user, different device:', newStreet);
          }
          
          onStreetInserted?.(newStreet);
        }
      );

      channel.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'd2d_streets',
          filter: `area_id=eq.${currentAreaId}`,
        },
        (payload) => {
          const updatedStreet = payload.new as D2DStreet;
          
          if (recentlyCreatedItems.has(`update-${updatedStreet.id}`)) {
            return;
          }
          
          if (updatedStreet.user_id !== userId) {
            console.log('🛣️ Street updated by another user:', updatedStreet);
          }
          onStreetUpdated?.(updatedStreet);
        }
      );
    }

    // Listen for house changes when viewing a street
    if (currentStreetId) {
      channel.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'd2d_houses',
          filter: `street_id=eq.${currentStreetId}`,
        },
        (payload) => {
          const newHouse = payload.new as D2DHouse;
          
          if (recentlyCreatedItems.has(newHouse.id)) {
            console.log('🏠 Skipping house we just created:', newHouse.id);
            return;
          }
          
          if (newHouse.user_id !== userId) {
            console.log('🏠 New house created by another user:', newHouse);
            toast({
              title: "Neue Hausnummer",
              description: `Ein Teammitglied hat Hausnummer ${newHouse.house_number} erstellt.`,
            });
          } else {
            console.log('🏠 New house from same user, different device:', newHouse);
          }
          
          onHouseInserted?.(newHouse);
        }
      );

      channel.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'd2d_houses',
          filter: `street_id=eq.${currentStreetId}`,
        },
        (payload) => {
          const updatedHouse = payload.new as D2DHouse;
          
          if (recentlyCreatedItems.has(`update-${updatedHouse.id}`)) {
            return;
          }
          
          if (updatedHouse.user_id !== userId) {
            console.log('🏠 House updated by another user:', updatedHouse);
          }
          onHouseUpdated?.(updatedHouse);
        }
      );
    }

    // Listen for unit changes when viewing a house
    if (currentHouseId) {
      channel.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'd2d_units',
          filter: `house_id=eq.${currentHouseId}`,
        },
        (payload) => {
          const newUnit = payload.new as D2DUnit;
          
          if (recentlyCreatedItems.has(newUnit.id)) {
            console.log('🏢 Skipping unit we just created:', newUnit.id);
            return;
          }
          
          if (newUnit.user_id !== userId) {
            console.log('🏢 New unit created by another user:', newUnit);
            toast({
              title: "Neue Wohneinheit",
              description: `Ein Teammitglied hat ${newUnit.unit_name || 'eine neue Einheit'} erstellt.`,
            });
          } else {
            console.log('🏢 New unit from same user, different device:', newUnit);
          }
          
          onUnitInserted?.(newUnit);
        }
      );

      channel.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'd2d_units',
          filter: `house_id=eq.${currentHouseId}`,
        },
        (payload) => {
          const updatedUnit = payload.new as D2DUnit;
          
          if (recentlyCreatedItems.has(`update-${updatedUnit.id}`)) {
            return;
          }
          
          if (updatedUnit.user_id !== userId) {
            console.log('🏢 Unit updated by another user:', updatedUnit);
          }
          onUnitUpdated?.(updatedUnit);
        }
      );
    }

    channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        console.log('✅ Realtime sync channel subscribed');
      }
    });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        console.log('🔌 Cleaning up realtime sync channel');
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [userId, currentAreaId, currentStreetId, currentHouseId, onStreetInserted, onStreetUpdated, onHouseInserted, onHouseUpdated, onUnitInserted, onUnitUpdated]);
};
